import { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from "react";
import config from "../config";
import orcaLogo from "../Images/ORCA_SIDEBAR.png";

const API = config.API_BASE_URL;

// ── helpers ────────────────────────────────────────────────────────────────
function fmtTime(ts) {
  return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
function fmtDay(ts) {
  const d = new Date(ts), t = new Date(), y = new Date(t);
  y.setDate(t.getDate() - 1);
  if (d.toDateString() === t.toDateString()) return "Today";
  if (d.toDateString() === y.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

// ── normalize raw message from backend (handles PascalCase & camelCase) ────
function norm(m) {
  return {
    messageId: m.messageId ?? m.MessageId,
    user: m.user ?? m.User ?? "",
    admin: m.admin ?? m.Admin ?? "",
    content: m.content ?? m.Content ?? "",
    timestamp: m.timestamp ?? m.Timestamp ?? m.createdDate ?? m.CreatedDate,
    senderType: m.senderType ?? m.SenderType ?? "user",
    read: m.read ?? m.Read ?? false,
    likes: m.likes ?? m.Likes ?? 0,
    reactionType: m.reactionType ?? m.ReactionType ?? null,
    fileData: m.fileData ?? m.FileData ?? null,
    fileName: m.fileName ?? m.FileName ?? null,
    fileType: m.fileType ?? m.FileType ?? null,
  };
}

const EMOJIS = [
  { e: "❤️", l: "Love", c: "#f33e58" },
  { e: "😂", l: "Haha", c: "#f7b125" },
  { e: "😮", l: "Wow", c: "#f7b125" },
  { e: "😢", l: "Sad", c: "#f7b125" },
  { e: "😡", l: "Angry", c: "#e9710f" },
  { e: "👍", l: "Like", c: "#0084ff" },
];

// ── Reaction Picker ─────────────────────────────────────────────────────────
function Picker({ onPick, onClose, isUser, active }) {
  return (
    <div onMouseLeave={onClose} style={{
      position: "absolute", bottom: "calc(100% + 8px)",
      [isUser ? "right" : "left"]: -8,
      background: "#fff", border: "1px solid rgba(0,0,0,0.1)",
      borderRadius: 50, padding: "6px 10px",
      display: "flex", gap: 2,
      boxShadow: "0 8px 24px rgba(0,0,0,0.18)", zIndex: 999,
      animation: "um-pop 0.18s cubic-bezier(.34,1.56,.64,1)",
    }}>
      {EMOJIS.map(({ e, l, c }) => (
        <button key={e} onClick={() => { onPick(e); onClose(); }} title={l}
          style={{
            background: active === e ? `${c}22` : "none",
            border: "none", cursor: "pointer", fontSize: 24,
            width: 42, height: 42, borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
            transform: active === e ? "scale(1.2)" : "scale(1)",
            transition: "transform 0.15s",
          }}
          onMouseEnter={e2 => e2.currentTarget.style.transform = "scale(1.45) translateY(-6px)"}
          onMouseLeave={e2 => e2.currentTarget.style.transform = active === e ? "scale(1.2)" : "scale(1)"}
        >{e}</button>
      ))}
    </div>
  );
}

// ── Reply bar (shows above input) ───────────────────────────────────────────
function ReplyBar({ msg, onCancel }) {
  if (!msg) return null;
  const preview = msg.content?.startsWith("[IMAGE]")
    ? "📷 Photo"
    : msg.content?.slice(0, 60) ?? "";
  return (
    <div style={{
      padding: "8px 14px", background: "#e8f0ff",
      borderTop: "2px solid #0084ff",
      display: "flex", alignItems: "center", gap: 10, flexShrink: 0,
    }}>
      <div style={{ width: 3, borderRadius: 3, alignSelf: "stretch", background: "#0084ff", flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#0084ff" }}>
          Replying to {msg.senderType === "user" ? "yourself" : "Support"}
        </div>
        <div style={{ fontSize: 12, color: "#65676b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {preview}
        </div>
      </div>
      <button onClick={onCancel}
        style={{ background: "rgba(0,0,0,0.07)", border: "none", borderRadius: "50%", width: 24, height: 24, cursor: "pointer", fontSize: 12, fontWeight: 900 }}>
        ✕
      </button>
    </div>
  );
}

// ── Single message bubble ────────────────────────────────────────────────────
function Bubble({ msg, prev, next, onReact, onReply }) {
  const [hovered, setHovered] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const hideTimer = useRef(null);

  const isUser = msg.senderType === "user";
  const samePrev = prev?.senderType === msg.senderType;
  const sameNext = next?.senderType === msg.senderType;
  const showTime = !next || (new Date(next.timestamp) - new Date(msg.timestamp)) > 5 * 60 * 1000;
  const reaction = msg.reaction ?? null;

  // image
  const isImage = msg.content?.startsWith("[IMAGE]");
  let imgSrc = null;
  if (isImage) {
    const raw = msg.content.replace("[IMAGE]", "").trim();
    imgSrc = raw.startsWith("data:") || raw.startsWith("http") ? raw : `data:image/jpeg;base64,${raw}`;
  }

  // reply quote
  let quotedText = null, quotedBy = null, actualText = msg.content ?? "";
  if (!isImage && actualText.startsWith("↩")) {
    const m = actualText.match(/^↩ "(.*?)" — (.*)$/s);
    if (m) { quotedText = m[1]; actualText = m[2]; quotedBy = isUser ? "Support" : "You"; }
  }

  const onEnter = () => { setHovered(true); clearTimeout(hideTimer.current); };
  const onLeave = () => {
    setHovered(false);
    hideTimer.current = setTimeout(() => setShowPicker(false), 250);
  };

  const bg = isUser ? "#0084ff" : "#f0f2f5";
  const fg = isUser ? "#fff" : "#050505";
  const br = isUser
    ? `18px 4px ${sameNext ? "4px" : "18px"} 18px`
    : `4px 18px 18px ${sameNext ? "4px" : "18px"}`;

  return (
    <div style={{
      display: "flex", flexDirection: isUser ? "row-reverse" : "row",
      alignItems: "flex-end", gap: 6,
      marginTop: samePrev ? 2 : 12,
      marginBottom: reaction ? 20 : 0,
    }}>
      {/* Support avatar */}
      {!isUser && (
        <div style={{ width: 28, flexShrink: 0, alignSelf: "flex-end" }}>
          {!sameNext && (
            <div style={{
              width: 28, height: 28, borderRadius: "50%",
              background: "linear-gradient(135deg,#0084ff,#00c6ff)",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13,
            }}>🎧</div>
          )}
        </div>
      )}

      <div style={{ maxWidth: "70%", display: "flex", flexDirection: "column", alignItems: isUser ? "flex-end" : "flex-start" }}>
        {!isUser && !samePrev && (
          <span style={{ fontSize: 10, color: "#8a8d91", marginBottom: 2, marginLeft: 4 }}>Support Agent</span>
        )}

        <div style={{ display: "flex", flexDirection: isUser ? "row-reverse" : "row", alignItems: "center", gap: 4, position: "relative" }}
          onMouseEnter={onEnter} onMouseLeave={onLeave}>

          {/* Picker */}
          {showPicker && (
            <Picker isUser={isUser} active={reaction}
              onPick={emoji => onReact(msg.messageId, emoji)}
              onClose={() => setShowPicker(false)} />
          )}

          {/* Bubble */}
          <div style={{ position: "relative" }}>
            {isImage ? (
              <div style={{ borderRadius: 12, overflow: "hidden", maxWidth: 220, cursor: "pointer" }}
                onClick={() => window.open(imgSrc, "_blank")}>
                <img src={imgSrc} alt="img" style={{ width: "100%", display: "block" }} />
              </div>
            ) : (
              <div style={{ background: bg, color: fg, borderRadius: br, padding: quotedText ? "10px 13px 9px" : "9px 13px", fontSize: 14, lineHeight: 1.45, wordBreak: "break-word", boxShadow: isUser ? "0 1px 4px rgba(0,132,255,0.25)" : "0 1px 2px rgba(0,0,0,0.08)" }}>
                {quotedText && (
                  <div style={{ borderLeft: `3px solid ${isUser ? "rgba(255,255,255,0.6)" : "#0084ff"}`, paddingLeft: 8, marginBottom: 6, opacity: 0.85 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: isUser ? "rgba(255,255,255,0.9)" : "#0084ff", marginBottom: 1 }}>{quotedBy}</div>
                    <div style={{ fontSize: 11.5, color: isUser ? "rgba(255,255,255,0.8)" : "#65676b" }}>{quotedText.slice(0, 80)}</div>
                  </div>
                )}
                {actualText}
              </div>
            )}

            {/* Reaction badge */}
            {reaction && (
              <div onClick={() => onReact(msg.messageId, reaction)}
                style={{
                  position: "absolute", bottom: -16, [isUser ? "left" : "right"]: 4,
                  background: "#fff", border: "1px solid #e4e6eb",
                  borderRadius: 20, padding: "2px 8px 2px 4px",
                  fontSize: 13, boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                  zIndex: 5, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 3,
                  animation: "um-pop 0.2s cubic-bezier(.34,1.56,.64,1)",
                }}>
                <span>{reaction}</span>
                <span style={{ fontSize: 11, color: "#65676b", fontWeight: 600 }}>1</span>
              </div>
            )}
          </div>

          {/* Action buttons on hover */}
          {hovered && (
            <div style={{ display: "flex", flexDirection: isUser ? "row-reverse" : "row", gap: 3, alignItems: "center" }}>
              {[
                { icon: "😊", title: "React", fn: () => setShowPicker(v => !v) },
                { icon: "↩", title: "Reply", fn: () => onReply(msg) },
              ].map(b => (
                <button key={b.title} onClick={b.fn} title={b.title}
                  style={{ background: "#fff", border: "1px solid #e4e6eb", borderRadius: "50%", width: 30, height: 30, cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 1px 4px rgba(0,0,0,0.1)" }}>
                  {b.icon}
                </button>
              ))}
            </div>
          )}
        </div>

        {showTime && (
          <span style={{ fontSize: 10, color: "#8a8d91", marginTop: reaction ? 20 : 3, padding: "0 4px" }}>
            {fmtTime(msg.timestamp)}{isUser && msg.read && " · Seen ✓"}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Main UserMessenger component ────────────────────────────────────────────
const UserMessenger = forwardRef(({ userData, isDarkMode }, ref) => {
  const username = userData?.username ?? null;

  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [reactions, setReactions] = useState({});   // optimistic local reactions
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adminName, setAdminName] = useState(null);
  const [unread, setUnread] = useState(0);
  const [imgFile, setImgFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [replyTo, setReplyTo] = useState(null);

  const openRef = useRef(false);
  const pollRef = useRef(null);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  const inputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    openExpanded: () => { setOpen(true); setExpanded(true); openRef.current = true; doMarkRead(); },
  }));

  // ── fetch messages ─────────────────────────────────────────
  const fetchMsgs = useCallback(async () => {
    if (!username) { setLoading(false); return; }
    try {
      const res = await fetch(`${API}/Messages/user/${encodeURIComponent(username)}`);
      if (!res.ok) { console.error("fetchMsgs failed:", res.status, res.statusText); return; }
      const raw = await res.json();
      const data = (Array.isArray(raw) ? raw : (raw.data ?? [])).map(norm);
      data.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      setMsgs(data);
      // pick admin name from any admin message
      const am = data.find(m => m.senderType === "admin" && m.admin);
      if (am) setAdminName(am.admin);
      // update unread badge only when closed
      if (!openRef.current) {
        setUnread(data.filter(m => m.senderType === "admin" && !m.read).length);
      }
    } catch (err) {
      console.error("fetchMsgs error:", err);
    } finally {
      setLoading(false);
    }
  }, [username]);

  // ── mark admin messages as read ────────────────────────────
  const doMarkRead = useCallback(async () => {
    if (!username) return;
    try {
      await fetch(`${API}/Messages/markAsReadByUser/${encodeURIComponent(username)}`, { method: "POST" });
      setUnread(0);
    } catch { }
  }, [username]);

  // ── polling ────────────────────────────────────────────────
  useEffect(() => {
    fetchMsgs();
    pollRef.current = setInterval(fetchMsgs, 4000);
    return () => clearInterval(pollRef.current);
  }, [fetchMsgs]);

  // ── scroll to bottom when open ─────────────────────────────
  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, open]);

  // ── focus input on reply ───────────────────────────────────
  useEffect(() => { if (replyTo) inputRef.current?.focus(); }, [replyTo]);

  const openChat = () => {
    setOpen(true); openRef.current = true;
    doMarkRead();
  };
  const closeChat = () => {
    setOpen(false); openRef.current = false;
    setExpanded(false); setReplyTo(null);
  };

  // ── send text ──────────────────────────────────────────────
  const sendText = async (fixedContent) => {
    const text = fixedContent ?? input.trim();
    if (!text || sending) return;
    const target = adminName ?? "admin";

    let body = text;
    if (!fixedContent && replyTo) {
      const q = replyTo.content?.startsWith("[IMAGE]") ? "📷 Photo" : replyTo.content?.slice(0, 60);
      body = `↩ "${q}" — ${text}`;
    }

    setSending(true);
    try {
      const res = await fetch(`${API}/Messages/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user: username, admin: target, content: body }),
      });
      if (!res.ok) { console.error("send failed:", res.status, await res.text()); return; }
      if (!fixedContent) { setInput(""); setReplyTo(null); }
      await fetchMsgs();
    } catch (err) { console.error("sendText error:", err); }
    finally { setSending(false); }
  };

  // ── send image ─────────────────────────────────────────────
  const sendImage = async () => {
    if (!imgFile || uploading) return;
    setUploading(true);
    const target = adminName ?? "admin";
    try {
      const b64 = await new Promise(res => {
        const r = new FileReader();
        r.onload = ev => res(ev.target.result);
        r.readAsDataURL(imgFile);
      });
      // upload for filename reference (optional)
      let fileName = imgFile.name;
      try {
        const fd = new FormData(); fd.append("file", imgFile);
        const up = await fetch(`${API}/Messages/uploadFile`, { method: "POST", body: fd });
        if (up.ok) { const d = await up.json(); fileName = d.fileName ?? fileName; }
      } catch { }

      const res = await fetch(`${API}/Messages/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user: username, admin: target,
          content: `[IMAGE]${b64}`,
          fileData: b64.split(",")[1], fileName, fileType: imgFile.type, fileSize: imgFile.size,
        }),
      });
      if (!res.ok) { console.error("sendImage failed:", res.status, await res.text()); return; }
      setImgFile(null);
      await fetchMsgs();
    } catch (err) { console.error("sendImage error:", err); }
    finally { setUploading(false); }
  };

  // ── react ──────────────────────────────────────────────────
  const handleReact = async (messageId, emoji) => {
    // optimistic toggle
    setReactions(prev => ({ ...prev, [messageId]: prev[messageId] === emoji ? null : emoji }));
    try {
      await fetch(`${API}/Messages/react/${messageId}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reactionType: emoji }),
      });
    } catch { }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); imgFile ? sendImage() : sendText(); }
    if (e.key === "Escape") setReplyTo(null);
  };

  // merge local reactions
  const displayed = msgs.map(m => ({
    ...m,
    reaction: reactions[m.messageId] !== undefined
      ? reactions[m.messageId]
      : m.reactionType ?? (m.likes ? "❤️" : null),
  }));

  const canSend = (input.trim() || imgFile) && !sending && !uploading;

  // ── styles ─────────────────────────────────────────────────
  const winStyle = expanded ? {
    position: "fixed", top: "50%", left: "50%",
    transform: "translate(-50%,-50%)",
    width: "min(860px,95vw)", height: "88vh", zIndex: 10002,
  } : {
    position: "absolute", bottom: 68, right: 0,
    width: 360, height: 530,
  };

  return (
    <>
      <style>{`
        @keyframes um-pop    { from{transform:scale(0.6);opacity:0} to{transform:scale(1);opacity:1} }
        @keyframes um-up     { from{transform:translateY(12px);opacity:0} to{transform:translateY(0);opacity:1} }
        @keyframes um-spin   { to{transform:rotate(360deg)} }
        .um-scroll::-webkit-scrollbar{width:4px}
        .um-scroll::-webkit-scrollbar-thumb{background:#c4c9d4;border-radius:4px}
        .um-scroll::-webkit-scrollbar-track{background:transparent}
      `}</style>

      {/* expanded backdrop */}
      {open && expanded && (
        <div onClick={closeChat}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10001, animation: "um-pop 0.2s ease" }} />
      )}

      <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 10002 }}>

        {/* ── Chat window ── */}
        {open && (
          <div style={{
            ...winStyle, display: "flex", flexDirection: "column",
            background: "#fff", borderRadius: expanded ? 20 : 18,
            border: "1px solid #e4e6eb",
            boxShadow: expanded ? "0 28px 80px rgba(0,0,0,0.35)" : "0 10px 40px rgba(0,0,0,0.2)",
            overflow: "hidden", animation: "um-up 0.2s ease",
          }}>

            {/* Header */}
            <div style={{
              padding: expanded ? "14px 20px" : "12px 14px",
              background: "linear-gradient(135deg,#0084ff,#006fde)",
              display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ position: "relative" }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: "50%",
                    background: "rgba(255,255,255,0.2)", border: "2px solid rgba(255,255,255,0.4)",
                    display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
                  }}>
                    <img src={orcaLogo} alt="ORCA" style={{ width: 32, height: 32, objectFit: "contain" }} />
                  </div>
                  <span style={{ position: "absolute", bottom: 1, right: 1, width: 11, height: 11, borderRadius: "50%", background: "#31a24c", border: "2px solid #0084ff" }} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#fff" }}>ORCA Support</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.85)" }}>
                    {adminName ? `${adminName} is handling your chat` : "● Online · replies in minutes"}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => setExpanded(v => !v)}
                  style={{ background: "rgba(255,255,255,0.18)", border: "none", color: "#fff", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {expanded
                    ? <svg width="15" height="15" fill="#fff" viewBox="0 0 24 24"><path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" /></svg>
                    : <svg width="15" height="15" fill="#fff" viewBox="0 0 24 24"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" /></svg>}
                </button>
                <button onClick={closeChat}
                  style={{ background: "rgba(255,255,255,0.18)", border: "none", color: "#fff", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  ✕
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="um-scroll" style={{ flex: 1, overflowY: "auto", padding: expanded ? "16px 22px" : "12px 14px", background: "#fff", display: "flex", flexDirection: "column" }}>
              {loading ? (
                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 10, color: "#8a8d91" }}>
                  <div style={{ width: 26, height: 26, borderRadius: "50%", border: "3px solid #ddd", borderTop: "3px solid #0084ff", animation: "um-spin 0.8s linear infinite" }} />
                  Loading...
                </div>
              ) : displayed.length === 0 ? (
                <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
                  <div style={{ width: 64, height: 64, borderRadius: "50%", background: "linear-gradient(135deg,#0084ff,#00c6ff)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>🎧</div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: "#050505" }}>ORCA Support</div>
                  <div style={{ fontSize: 13, color: "#8a8d91", textAlign: "center", lineHeight: 1.6 }}>
                    Hi <strong>{userData?.fullNames?.split(" ")[0] ?? username}</strong>! 👋<br />How can we help?
                  </div>
                </div>
              ) : (
                displayed.map((msg, i) => {
                  const prevMsg = displayed[i - 1];
                  const nextMsg = displayed[i + 1];
                  const showDay = i === 0 || fmtDay(prevMsg?.timestamp) !== fmtDay(msg.timestamp);
                  return (
                    <div key={msg.messageId ?? i}>
                      {showDay && (
                        <div style={{ textAlign: "center", margin: "10px 0 6px" }}>
                          <span style={{ background: "#e4e6eb", color: "#8a8d91", fontSize: 11, fontWeight: 600, padding: "3px 12px", borderRadius: 20 }}>
                            {fmtDay(msg.timestamp)}
                          </span>
                        </div>
                      )}
                      <Bubble msg={msg} prev={prevMsg} next={nextMsg} onReact={handleReact} onReply={setReplyTo} />
                    </div>
                  );
                })
              )}
              <div ref={bottomRef} />
            </div>

            {/* Reply bar */}
            <ReplyBar msg={replyTo} onCancel={() => setReplyTo(null)} />

            {/* Image preview */}
            {imgFile && (
              <div style={{ padding: "6px 14px 4px", borderTop: "1px solid #e4e6eb", background: "#fff", flexShrink: 0 }}>
                <div style={{ position: "relative", display: "inline-block" }}>
                  <img src={URL.createObjectURL(imgFile)} alt="preview"
                    style={{ height: 68, borderRadius: 10, border: "2px solid #0084ff", objectFit: "cover" }} />
                  <button onClick={() => setImgFile(null)}
                    style={{ position: "absolute", top: -6, right: -6, background: "#e41e3f", color: "#fff", border: "2px solid #fff", borderRadius: "50%", width: 20, height: 20, cursor: "pointer", fontSize: 10, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    ✕
                  </button>
                </div>
              </div>
            )}

            {/* Input bar */}
            <div style={{
              padding: expanded ? "10px 16px" : "8px 12px",
              borderTop: "1px solid #e4e6eb", background: "#fff",
              display: "flex", alignItems: "flex-end", gap: 8, flexShrink: 0,
            }}>
              {/* Image button */}
              <button onClick={() => fileRef.current?.click()}
                style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 2px", display: "flex", alignItems: "center" }}>
                <svg width="22" height="22" fill="none" stroke="#0084ff" viewBox="0 0 24 24" strokeWidth={1.8}>
                  <rect x="3" y="3" width="18" height="18" rx="3" />
                  <circle cx="8.5" cy="8.5" r="1.5" fill="#0084ff" stroke="none" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 15l-5-5L5 21" />
                </svg>
              </button>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
                onChange={e => { const f = e.target.files[0]; if (f) setImgFile(f); e.target.value = ""; }} />

              {/* Text input */}
              <div style={{ flex: 1, background: "#f0f2f5", borderRadius: 22, padding: "10px 16px" }}>
                <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey}
                  placeholder={imgFile ? "Add a caption..." : replyTo ? "Write a reply..." : "Aa"}
                  rows={1}
                  style={{ width: "100%", border: "none", outline: "none", background: "transparent", resize: "none", fontSize: 14, color: "#050505", lineHeight: 1.4, maxHeight: 120, overflow: "auto", fontFamily: "system-ui,sans-serif" }} />
              </div>

              {/* Send / Like button */}
              {canSend ? (
                <button onClick={imgFile ? sendImage : () => sendText()}
                  style={{ background: "#0084ff", border: "none", borderRadius: "50%", width: 38, height: 38, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0, boxShadow: "0 2px 8px rgba(0,132,255,0.4)" }}>
                  {sending || uploading
                    ? <div style={{ width: 15, height: 15, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.3)", borderTop: "2px solid #fff", animation: "um-spin 0.8s linear infinite" }} />
                    : <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" /></svg>}
                </button>
              ) : (
                <button onClick={() => sendText("👍")}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 24, color: "#0084ff", flexShrink: 0 }}>
                  👍
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Floating bubble ── */}
        <button onClick={open ? closeChat : openChat}
          style={{
            width: 56, height: 56, borderRadius: "50%",
            background: "linear-gradient(135deg,#0084ff,#006fde)",
            border: "none", cursor: "pointer",
            boxShadow: "0 4px 20px rgba(0,132,255,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "transform 0.2s", position: "relative", zIndex: 10002,
          }}
          onMouseEnter={e => e.currentTarget.style.transform = "scale(1.1)"}
          onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
        >
          {open
            ? <svg width="22" height="22" fill="#fff" viewBox="0 0 24 24"><path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" /></svg>
            : <svg width="26" height="26" fill="#fff" viewBox="0 0 24 24"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" /></svg>}

          {!open && unread > 0 && (
            <div style={{
              position: "absolute", top: -4, right: -4,
              background: "#e41e3f", color: "#fff", borderRadius: "50%",
              width: 20, height: 20, fontSize: 10, fontWeight: 800,
              display: "flex", alignItems: "center", justifyContent: "center",
              border: "2px solid #fff",
            }}>
              {unread > 9 ? "9+" : unread}
            </div>
          )}
        </button>
      </div>
    </>
  );
});

UserMessenger.displayName = "UserMessenger";
export default UserMessenger;